import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { watchCollection } from '../../api/document-api/DocumentAPI';

// Option type for the autocomplete
interface AutocompleteOption {
  dependentOf: string | null;
  label: string;
  value: string;
}

interface FilterAutocompleteProps {
  label: string;
  selected: AutocompleteOption | null;
  onChange: (event: SyntheticEvent, value: AutocompleteOption | null) => void;
  filter?: (item: AutocompleteOption) => boolean;
  noOptionsText?: string;
  disabled?: boolean;
  path: string;
}

/**
 * FilterAutocomplete is a component that allows the user to select an option from a list of options and
 * applies passed filters.
 * @param label The label for the autocomplete
 * @param selected The selected option
 * @param onChange The function to call when the user selects an option
 * @param filter The function to filter the options
 * @param noOptionsText The text to display when there are no options
 * @param disabled Whether the autocomplete is disabled
 * @param path The path to the collection to watch
 */
// eslint-disable-next-line import/prefer-default-export
export const FilterAutocomplete: React.FC<FilterAutocompleteProps> = function ({
  label,
  selected,
  onChange,
  path,
  filter,
  noOptionsText = 'No options found',
  disabled = false,
}) {
  const [items, setItems] = useState<AutocompleteOption[]>([]);

  useEffect(() => {
    const unsubscribe = watchCollection(
      () => {},
      (data: AutocompleteOption[]) => { setItems(data); },
      path,
    );
    return () => unsubscribe();
  }, []);

  const filteredItems = filter ? items.filter(filter) : items;

  const sortByName = (a: AutocompleteOption, b: AutocompleteOption) => a.label.localeCompare(b.label, 'en');

  return (
    <Autocomplete
      className="mt-2"
      disablePortal
      noOptionsText={noOptionsText}
      disabled={disabled}
      renderInput={(params) => (
        <TextField {...params} label={label} />
      )}
      value={selected}
      onChange={onChange}
      getOptionLabel={(option: AutocompleteOption) => option.label}
      options={filteredItems.sort(sortByName)}
    />
  );
};
